import { useCallback, useEffect, useRef, useState } from "react";
import {
  readStoredPreference,
  writeStoredPreference,
} from "../../hooks/localPrefs";

export const PLAYBACK_SPEEDS = [0.25, 0.5, 1, 2, 4, 8] as const;
export type PlaybackSpeed = (typeof PLAYBACK_SPEEDS)[number];

function readStoredSpeed(key: string | undefined): PlaybackSpeed {
  if (key === undefined) {
    return 1;
  }
  const stored = readStoredPreference(key);
  const parsed = stored === null ? Number.NaN : Number(stored);
  return PLAYBACK_SPEEDS.includes(parsed as PlaybackSpeed)
    ? (parsed as PlaybackSpeed)
    : 1;
}

export function usePlaybackClock({
  cycleLength,
  initialValue,
  secondsPerCycleAtOneX,
  speedStorageKey,
}: {
  readonly cycleLength: number;
  readonly initialValue: number;
  readonly secondsPerCycleAtOneX: number;
  readonly speedStorageKey?: string;
}) {
  const [value, setValue] = useState(initialValue);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeedState] = useState<PlaybackSpeed>(() =>
    readStoredSpeed(speedStorageKey),
  );
  const frame = useRef<number | null>(null);
  const previous = useRef<number | null>(null);

  useEffect(() => {
    if (!isPlaying) {
      previous.current = null;
      return;
    }

    const tick = (timestamp: number) => {
      const last = previous.current;
      previous.current = timestamp;
      if (last !== null) {
        const elapsed = (timestamp - last) / 1_000;
        if (elapsed > 0 && elapsed <= 0.25) {
          const delta = (elapsed * speed * cycleLength) / secondsPerCycleAtOneX;
          setValue((current) => {
            const next = (current + delta) % cycleLength;
            return next < 0 ? next + cycleLength : next;
          });
        }
      }
      frame.current = requestAnimationFrame(tick);
    };

    frame.current = requestAnimationFrame(tick);
    return () => {
      if (frame.current !== null) {
        cancelAnimationFrame(frame.current);
        frame.current = null;
      }
      previous.current = null;
    };
  }, [cycleLength, isPlaying, secondsPerCycleAtOneX, speed]);

  const togglePlayback = useCallback(() => {
    setIsPlaying((current) => !current);
  }, []);

  const setSpeed = useCallback(
    (nextSpeed: PlaybackSpeed) => {
      setSpeedState(nextSpeed);
      if (speedStorageKey !== undefined) {
        writeStoredPreference(speedStorageKey, String(nextSpeed));
      }
    },
    [speedStorageKey],
  );

  return {
    isPlaying,
    setValue,
    speed,
    setSpeed,
    togglePlayback,
    value,
  };
}
